import { useId, useState } from 'react';

import { cx } from '@/lib/cx';
import { Popover } from '@/ui/primitives/popover';

import { ListaPesquisavel } from './select-pesquisavel-lista';
import { BotaoLimpar, CampoBusca, Gatilho } from './select-pesquisavel-partes';
import {
  type BuscarOpcoes,
  type FonteOpcoes,
  type OpcaoPesquisavel,
  opcaoAtual,
  teclaDaLista,
  useOpcoesPesquisaveis,
} from './use-opcoes-pesquisaveis';

export type { BuscarOpcoes, OpcaoPesquisavel } from './use-opcoes-pesquisaveis';

interface Base {
  valor: string | null;
  aoMudar: (valor: string | null, opcao: OpcaoPesquisavel | null) => void;
  /** Rótulo acessível do campo de busca ("Buscar cidade"). */
  rotuloBusca: string;
  placeholder?: string;
  vazio?: string;
  permitirLimpar?: boolean;
  disabled?: boolean;
  /** Texto mostrado no lugar do valor quando desabilitado (o porquê). */
  textoDesabilitado?: string;
  id?: string;
  'aria-label'?: string;
  'aria-invalid'?: true;
  'aria-describedby'?: string;
  className?: string;
  /** `sm`: altura compacta (barras de ação, linhas densas). */
  tamanho?: 'md' | 'sm';
}

/** Modo local: a lista inteira já está no cliente e é filtrada aqui. */
interface Local extends Base {
  opcoes: readonly OpcaoPesquisavel[];
}

/**
 * Modo remoto: busca no servidor a cada termo (debounce + cancelamento). `buscar` deve ser
 * ESTÁVEL (useCallback). `opcaoSelecionada` mostra o valor atual sem carregar lista nenhuma.
 */
interface Remoto extends Base {
  buscar: BuscarOpcoes;
  minCaracteres?: number;
  atrasoMs?: number;
  opcaoSelecionada?: OpcaoPesquisavel | null;
}

export type SelectPesquisavelProps = Local | Remoto;

function fonteDe(props: SelectPesquisavelProps): FonteOpcoes {
  if ('buscar' in props) {
    return {
      tipo: 'remoto',
      buscar: props.buscar,
      minCaracteres: props.minCaracteres ?? 0,
      atrasoMs: props.atrasoMs ?? 250,
    };
  }
  return { tipo: 'local', opcoes: props.opcoes };
}

function mostraLimpar(props: SelectPesquisavelProps, atual: OpcaoPesquisavel | null): boolean {
  return props.permitirLimpar === true && atual !== null && props.disabled !== true;
}

function textoDoGatilho(props: SelectPesquisavelProps, atual: OpcaoPesquisavel | null): string {
  if (props.disabled && props.textoDesabilitado) return props.textoDesabilitado;
  return atual?.rotulo ?? props.placeholder ?? 'Escolha';
}

/**
 * Escolha única com busca ("select2"). Padrão do sistema quando a lista passa de 10 opções ou
 * pode crescer; dados do servidor que podem ser muitos usam o modo remoto (docs/design-system).
 */
export function SelectPesquisavel(props: SelectPesquisavelProps) {
  const [aberto, setAberto] = useState(false);
  const [termo, setTermo] = useState('');
  const [ativa, setAtiva] = useState(-1);
  const [escolhida, setEscolhida] = useState<OpcaoPesquisavel | null>(null);
  const fonte = fonteDe(props);
  const { opcoes, estado, tentarDeNovo } = useOpcoesPesquisaveis(fonte, termo, aberto);
  const base = useId();
  const idOpcao = (i: number) => `${base}-opcao-${String(i)}`;
  const atual = opcaoAtual(props.valor, props, escolhida);

  const mudarAberto = (abrir: boolean) => {
    setAberto(abrir);
    setTermo('');
    setAtiva(-1);
  };
  const escolher = (opcao: OpcaoPesquisavel) => {
    setEscolhida(opcao);
    props.aoMudar(opcao.valor, opcao);
    mudarAberto(false);
  };

  return (
    <div className={cx('relative', props.className)}>
      <Popover
        aberto={aberto}
        aoMudar={mudarAberto}
        className="w-(--radix-popover-trigger-width) min-w-64 p-0"
        gatilho={
          <Gatilho
            aberto={aberto}
            texto={textoDoGatilho(props, atual)}
            apagado={!atual || props.disabled === true}
            comLimpar={mostraLimpar(props, atual)}
            tamanho={props.tamanho ?? 'md'}
            disabled={props.disabled === true}
            id={props.id}
            aria-label={props['aria-label']}
            aria-invalid={props['aria-invalid']}
            aria-describedby={props['aria-describedby']}
          />
        }
      >
        <CampoBusca
          aria-label={props.rotuloBusca}
          aria-controls={`${base}-lista`}
          aria-activedescendant={ativa >= 0 ? idOpcao(ativa) : undefined}
          value={termo}
          onChange={(e) => {
            setTermo(e.target.value);
            setAtiva(-1);
          }}
          onKeyDown={(e) => {
            const acao = teclaDaLista(e, ativa, opcoes.length);
            if (acao && 'ativa' in acao) setAtiva(acao.ativa);
            const opcao = acao && 'escolher' in acao ? opcoes[acao.escolher] : undefined;
            if (opcao) escolher(opcao);
          }}
        />
        <ListaPesquisavel
          idLista={`${base}-lista`}
          rotulo={props['aria-label'] ?? props.rotuloBusca}
          opcoes={opcoes}
          termo={termo}
          ativa={ativa}
          selecionado={props.valor}
          estado={estado}
          minCaracteres={fonte.tipo === 'remoto' ? fonte.minCaracteres : 0}
          vazio={props.vazio ?? 'Nada encontrado.'}
          idOpcao={idOpcao}
          aoEscolher={escolher}
          aoApontar={setAtiva}
          tentarDeNovo={tentarDeNovo}
        />
      </Popover>
      {mostraLimpar(props, atual) && (
        <BotaoLimpar
          rotulo={`Limpar ${props['aria-label'] ?? ''}`.trim()}
          aoLimpar={() => {
            setEscolhida(null);
            props.aoMudar(null, null);
          }}
        />
      )}
    </div>
  );
}
